import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { HelpCircle } from "lucide-react";
import { Card } from "@/components/Card/Card";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SpendVsRevenueChart as SpendVsRevenueChartType } from "../../api/fetchROIDashboardData/types";
import { formatCurrency } from "@/modules/stochastic/features/SalesAndCustomerDashboard/utils/chart";
import { useTheme } from "@/context/ThemeContext";

interface SpendVsRevenueChartProps {
  data: SpendVsRevenueChartType | null;
}

const formatYAxis = (value: number): string => {
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}k`;
  }
  return `$${value}`;
};

export const SpendVsRevenueChart: React.FC<SpendVsRevenueChartProps> = ({
  data,
}) => {
  const { theme } = useTheme();

  if (!data) {
    return null;
  }

  const isDark = theme === "dark";

  return (
    <Card className="w-full">
      <div className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Spend vs. Revenue
          </h3>
          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>
                  Comparison of direct mail marketing spend vs matchback revenue
                  (sales from customers acquired through direct mail) for{" "}
                  {data.year}.
                </p>
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>

        <div className="h-80">
          <ResponsiveContainer height="100%" width="100%">
            <BarChart
              data={data.data}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                axisLine={false}
                dataKey="month"
                tick={{ fill: isDark ? "#94a3b8" : "#6B7280", fontSize: 12 }}
                tickLine={false}
              />
              <YAxis
                axisLine={false}
                tick={{ fill: isDark ? "#94a3b8" : "#6B7280", fontSize: 12 }}
                tickFormatter={formatYAxis}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDark ? "#1e293b" : "white",
                  border: `1px solid ${isDark ? "#334155" : "#E5E7EB"}`,
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  color: isDark ? "#f1f5f9" : "#1f2937",
                }}
                formatter={(value: number) => [formatCurrency(value)]}
                labelStyle={{ fontWeight: 600, marginBottom: 4 }}
              />
              <Legend
                formatter={(value) => (
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {value}
                  </span>
                )}
                iconType="rect"
                wrapperStyle={{ paddingTop: 20 }}
              />
              <Bar
                dataKey="spend"
                fill="#EF4444"
                name="Spend"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="matchbackRevenue"
                fill="#10B981"
                name="Matchback Revenue"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Card>
  );
};
